/*
 * preview.js — живой предпросмотр /video-stabilizer.
 *
 * <video> играет исходник, холст показывает КАЖДЫЙ его кадр так, как он
 * выйдет в файле: тот же VideoStabilizer.createRenderer, что у экспорта, та же
 * поправка по номеру кадра. Онлайн-стабилизаторы показывают результат только
 * после серверной обработки (исследование 30.09: CapCut стабилизирует на
 * экспорте, Kapwing и 123apps — после загрузки); здесь он играет до экспорта.
 *
 *   var p = VSPreview.create({ stage, canvas, video, onFrame, onPlayState });
 *   p.load(url)                         → Promise<{ W, H, duration }>
 *   p.setPlan({ plan, times, fill })     — план готов (или сменился уровень)
 *   p.setView('result' | 'split' | 'original'); p.setSplit(0..1)
 *   p.setGuides(bool)                    — сетка горизонта, пока крутят наклон
 *   p.play() / p.pause() / p.toggle() / p.seek(t) / p.step(±1) / p.render()
 *   p.destroy()
 *
 * Номер кадра — из времени показа (`requestVideoFrameCallback` → mediaTime,
 * Chrome 83+, Safari 15.4+, Firefox 132+; старее — rAF + currentTime) и шкалы
 * времён прохода разбора. Первый кадр конвейер прижимает к нулю, поэтому для
 * сверки его время восстанавливается по шагу соседей, а постоянный сдвиг двух
 * шкал снимается по первому показанному кадру.
 */
(function () {
'use strict';
if (window.VSPreview) return;

function median(a) {
    if (!a.length) return 0;
    var s = a.slice().sort(function (x, y) { return x - y; });
    return s[s.length >> 1];
}

function create(o) {
    var video = o.video, canvas = o.canvas, stage = o.stage;
    var VS = window.VideoStabilizer;
    var R = VS.createRenderer();
    var ctx = canvas.getContext('2d', { alpha: false });
    var st = {
        plan: null, tl: null, fill: 'crop', view: 'result', split: 0.5,
        W: 0, H: 0, offset: 0, calibrated: false, step: 1 / 30, lastIdx: 0, guides: false
    };
    var hasRVFC = typeof HTMLVideoElement !== 'undefined' && 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
    var rvfc = 0, raf = 0, dead = false, ro = null;

    /* Холст по месту: сторона по ширине сцены × плотность экрана, не больше
       исходника. Размер CSS держат max-width/max-height сцены, а пропорцию —
       сами размеры холста (как у картинки). */
    function fit() {
        if (!st.W || !st.H) return;
        var box = stage.getBoundingClientRect();
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var maxH = parseFloat(getComputedStyle(canvas).maxHeight) || box.height || st.H;
        var cssW = Math.min(box.width || st.W, maxH * st.W / st.H);
        var w = Math.max(2, Math.round(Math.min(st.W, cssW * dpr)));
        var h = Math.max(2, Math.round(w * st.H / st.W));
        // CSS-размер — по месту на сцене, а не по исходнику: ролик 640×360
        // иначе стоял бы островком посреди колонки в 800 px.
        canvas.style.width = Math.round(cssW) + 'px';
        canvas.style.height = Math.round(cssW * st.H / st.W) + 'px';
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w; canvas.height = h;
            R.reset();
            draw(st.lastIdx);
        }
    }

    function idxAt(t) {
        var tl = st.tl;
        if (!tl || !tl.length) return 0;
        var x = t + st.offset, lo = 0, hi = tl.length - 1;
        if (x <= tl[0]) return 0;
        if (x >= tl[hi]) return hi;
        while (hi - lo > 1) {
            var m = (lo + hi) >> 1;
            if (tl[m] <= x) lo = m; else hi = m;
        }
        return (x - tl[lo] <= tl[hi] - x) ? lo : hi;
    }

    function quad(i) {
        var zoom = st.fill === 'synth' ? 1 : (st.plan.zoom || 1);
        var q = VS.cropQuad(st.plan, i, st.W, st.H, zoom);
        var kx = canvas.width / st.W, ky = canvas.height / st.H;
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        ctx.moveTo(q[0].x * kx, q[0].y * ky);
        for (var k = 1; k < 4; k++) ctx.lineTo(q[k].x * kx, q[k].y * ky);
        ctx.closePath();
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fill('evenodd');
        ctx.beginPath();
        ctx.moveTo(q[0].x * kx, q[0].y * ky);
        for (var j = 1; j < 4; j++) ctx.lineTo(q[j].x * kx, q[j].y * ky);
        ctx.closePath();
        ctx.lineWidth = Math.max(1.5, canvas.width / 480);
        ctx.setLineDash([6 * ctx.lineWidth, 4 * ctx.lineWidth]);
        ctx.strokeStyle = 'rgba(255,255,255,0.92)';
        ctx.stroke();
        ctx.restore();
    }

    function guides() {
        var w = canvas.width, h = canvas.height;
        ctx.save();
        ctx.lineWidth = Math.max(1, w / 900);
        ctx.strokeStyle = 'rgba(255,255,255,0.55)';
        ctx.beginPath();
        for (var k = 1; k <= 5; k++) { var y = Math.round(h * k / 6) + 0.5; ctx.moveTo(0, y); ctx.lineTo(w, y); }
        for (var m = 1; m <= 2; m++) { var x = Math.round(w * m / 3) + 0.5; ctx.moveTo(x, 0); ctx.lineTo(x, h); }
        ctx.stroke();
        ctx.restore();
    }

    function draw(i) {
        if (dead || !st.W || video.readyState < 2 || !canvas.width) return;
        i = i || 0;
        if (!st.plan) {
            R.plain(ctx, video, st.W, st.H, 1);
        } else if (st.view === 'original') {
            R.plain(ctx, video, st.W, st.H, 1);
            quad(i);
        } else {
            R.draw(ctx, video, st.W, st.H, st.plan, i, { fill: st.fill });
            if (st.view === 'split') {
                var sx = Math.round(canvas.width * st.split);
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, 0, sx, canvas.height);
                ctx.clip();
                // «До» — тот же кадр с тем же зумом: половины отличаются ТОЛЬКО тряской.
                R.plain(ctx, video, st.W, st.H, st.fill === 'synth' ? 1 : (st.plan.zoom || 1));
                ctx.restore();
            }
        }
        if (st.guides) guides();
        st.lastIdx = i;
        if (typeof o.onFrame === 'function') { try { o.onFrame(i, video.currentTime); } catch (_) {} }
    }

    function calibrate(t) {
        st.calibrated = true;
        if (!st.tl || !st.tl.length) return;
        var d = st.tl[0] - t;
        // Сдвиг шкал — доли кадра или задержка картинки, не секунды: иное — не
        // тот кадр, и лучше не сдвигать вовсе.
        st.offset = Math.abs(d) < 0.5 ? d : 0;
    }

    function onVF(now, meta) {
        rvfc = 0;
        if (dead) return;
        var t = meta && typeof meta.mediaTime === 'number' ? meta.mediaTime : video.currentTime;
        if (!st.calibrated && st.tl && t < st.step * 1.5) calibrate(t);
        draw(idxAt(t));
        arm();
    }
    function arm() {
        if (dead) return;
        if (hasRVFC) { if (!rvfc) rvfc = video.requestVideoFrameCallback(onVF); return; }
        if (!raf && !video.paused) raf = requestAnimationFrame(tick);
    }
    function tick() {
        raf = 0;
        if (dead) return;
        draw(idxAt(video.currentTime));
        if (!video.paused) raf = requestAnimationFrame(tick);
    }

    function onPlay() { arm(); if (o.onPlayState) o.onPlayState(true); }
    function onPause() { if (o.onPlayState) o.onPlayState(false); }
    function onSeeked() {
        R.reset();
        if (!hasRVFC) draw(idxAt(video.currentTime));
    }
    function onData() { fit(); draw(idxAt(video.currentTime)); }
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadeddata', onData);
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(function () { fit(); }); ro.observe(stage); }

    return {
        load: function (url) {
            st.W = st.H = 0; st.plan = null; st.tl = null; st.calibrated = false; st.offset = 0;
            R.reset();
            return new Promise(function (resolve, reject) {
                var done = false;
                function ok() {
                    if (done) return;
                    done = true;
                    st.W = video.videoWidth; st.H = video.videoHeight;
                    fit();
                    arm();
                    resolve({ W: st.W, H: st.H, duration: video.duration });
                }
                function bad() { if (done) return; done = true; reject(new Error('vs-preview-load')); }
                video.addEventListener('loadedmetadata', function h() { video.removeEventListener('loadedmetadata', h); if (video.videoWidth > 0) ok(); else bad(); });
                video.addEventListener('error', function h2() { video.removeEventListener('error', h2); bad(); });
                video.loop = true;
                video.playsInline = true;
                video.preload = 'auto';
                video.src = url;
                try { video.load(); } catch (_) {}
            });
        },
        setPlan: function (p) {
            st.plan = p.plan || null;
            st.fill = p.fill || 'crop';
            if (p.times && p.times.length) {
                var tl = p.times.slice(), gaps = [];
                for (var i = 2; i < Math.min(tl.length, 64); i++) gaps.push(tl[i] - tl[i - 1]);
                var g = median(gaps) || (1 / 30);
                st.step = g;
                // Первый кадр конвейер прижимает к нулю — время для сверки
                // восстанавливаем по шагу соседей.
                if (tl.length > 2 && tl[1] - tl[0] > g * 1.5) tl[0] = tl[1] - g;
                st.tl = tl;
                st.calibrated = false;
                if (video.currentTime < g * 1.5) calibrate(video.currentTime);
            }
            R.reset();
            draw(idxAt(video.currentTime));
        },
        setView: function (v) { st.view = v; R.reset(); draw(idxAt(video.currentTime)); },
        setSplit: function (x) { st.split = Math.max(0, Math.min(1, x)); if (video.paused) draw(st.lastIdx); },
        setGuides: function (on) { st.guides = !!on; if (video.paused) draw(st.lastIdx); },
        play: function () { var pr = video.play(); if (pr && pr.catch) pr.catch(function () {}); },
        pause: function () { video.pause(); },
        toggle: function () { if (video.paused) this.play(); else this.pause(); },
        seek: function (t) {
            var d = video.duration;
            if (!isFinite(d) || d <= 0) return;
            video.currentTime = Math.max(0, Math.min(d - 0.001, t));
        },
        step: function (dir) {
            video.pause();
            this.seek(video.currentTime + dir * st.step);
        },
        render: function () { fit(); draw(idxAt(video.currentTime)); },
        frameAt: function (t) { return idxAt(t); },
        get step_s() { return st.step; },
        destroy: function () {
            dead = true;
            if (rvfc && video.cancelVideoFrameCallback) { try { video.cancelVideoFrameCallback(rvfc); } catch (_) {} }
            if (raf) cancelAnimationFrame(raf);
            if (ro) { try { ro.disconnect(); } catch (_) {} }
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('seeked', onSeeked);
            video.removeEventListener('loadeddata', onData);
        }
    };
}

window.VSPreview = { create: create };
})();
